import React, { useEffect, useState } from "react";
import { NotificationService } from "../../api/generated/services/NotificationService";
import { NotificationBasicViewModel } from "../../api/generated/models/NotificationBasicViewModel";
import { NotificationFormModal } from "./NotificationFormModal";
import { DeliveryHistoryModal } from "./DeliveryHistoryModal";
import { ConfirmationModal } from "../ConfirmationModal";
import { Button } from "../Button";
import { Toast } from "../Toast";
import { Loader } from "../Loader";
import "../../api/openApiConfig";

const PAGE_SIZE = 25;

const formatDate = (value: string | null | undefined): string =>
  value ? new Date(value).toLocaleString("da-DK") : "—";

const SegmentedPushTab: React.FC = () => {
  const [notifications, setNotifications] = useState<
    NotificationBasicViewModel[]
  >([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [toast, setToast] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [historyTarget, setHistoryTarget] =
    useState<NotificationBasicViewModel | null>(null);
  const [sendTarget, setSendTarget] =
    useState<NotificationBasicViewModel | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<NotificationBasicViewModel | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    let ignore = false;
    const run = async () => {
      try {
        setLoading(true);
        const response =
          await NotificationService.postApiNotificationListNotifications({
            pagination: { pageNumber: pageNumber - 1, pageSize: PAGE_SIZE },
          });
        if (ignore) return;
        setNotifications(response.data ?? []);
        setTotal(response.total ?? 0);
        setError(null);
      } catch (err) {
        if (ignore) return;
        console.error("Error fetching notifications:", err);
        setError("Failed to fetch notifications.");
      } finally {
        if (!ignore) {
          setLoading(false);
          setInitialLoad(false);
        }
      }
    };
    run();
    return () => {
      ignore = true;
    };
  }, [pageNumber, refreshKey]);

  const handleSend = async () => {
    if (!sendTarget?.id) return;
    setActionLoading(true);
    try {
      await NotificationService.postApiNotificationSendNotification({
        notificationId: sendTarget.id,
      });
      setToast("Notification queued for sending.");
      setSendTarget(null);
    } catch (err: any) {
      console.error("Error sending notification:", err);
      setError(
        err?.body?.message || err?.message || "Failed to send the notification."
      );
      setSendTarget(null);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    setActionLoading(true);
    try {
      await NotificationService.postApiNotificationDeleteNotification({
        notificationId: deleteTarget.id,
      });
      setToast("Notification deleted.");
      setDeleteTarget(null);
      setRefreshKey((k) => k + 1);
    } catch (err: any) {
      console.error("Error deleting notification:", err);
      setError(
        err?.body?.message ||
          err?.message ||
          "Failed to delete the notification."
      );
      setDeleteTarget(null);
    } finally {
      setActionLoading(false);
    }
  };

  if (initialLoad) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      {toast && (
        <Toast message={toast} type="success" onClose={() => setToast(null)} />
      )}

      <div className="mb-6 flex justify-end">
        <Button
          onClick={() => {
            setEditingId(null);
            setFormOpen(true);
          }}
        >
          New notification
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm mb-6">
          {error}
        </div>
      )}

      <div className="bg-white rounded-2xl p-6">
        {loading ? (
          <div className="flex justify-center my-8">
            <Loader />
          </div>
        ) : notifications.length === 0 ? (
          <p className="text-sm text-gray-400 my-8 text-center">
            No notifications yet. Create one to send a push to a customer
            segment.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-default">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Created</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {notifications.map((notification) => (
                  <tr key={notification.id ?? ""}>
                    <td className="font-medium">{notification.name ?? "—"}</td>
                    <td>{formatDate(notification.createdAt)}</td>
                    <td>
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingId(notification.id ?? null);
                            setFormOpen(true);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setHistoryTarget(notification)}
                        >
                          History
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => setDeleteTarget(notification)}
                        >
                          Delete
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => setSendTarget(notification)}
                        >
                          Send
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <Button
              variant="outline"
              disabled={pageNumber === 1}
              onClick={() => setPageNumber((p) => p - 1)}
            >
              Previous
            </Button>
            <span className="text-sm">
              Page {pageNumber} of {totalPages}
            </span>
            <Button
              variant="outline"
              disabled={pageNumber === totalPages}
              onClick={() => setPageNumber((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      <NotificationFormModal
        isOpen={formOpen}
        notificationId={editingId}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setToast(editingId ? "Notification updated." : "Notification created.");
          setRefreshKey((k) => k + 1);
        }}
      />

      <DeliveryHistoryModal
        isOpen={historyTarget !== null}
        notificationId={historyTarget?.id ?? null}
        notificationName={historyTarget?.name ?? null}
        onClose={() => setHistoryTarget(null)}
      />

      <ConfirmationModal
        isOpen={sendTarget !== null}
        onClose={() => setSendTarget(null)}
        onConfirm={handleSend}
        title="Send notification?"
        message={`"${sendTarget?.name ?? ""}" will be pushed to every customer matching its audience filters. Check the audience (and any date filters) before sending.`}
        confirmText="Send"
        cancelText="Cancel"
        isLoading={actionLoading}
      />

      <ConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete notification?"
        message={`"${deleteTarget?.name ?? ""}" will be deleted.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={actionLoading}
      />
    </>
  );
};

export default SegmentedPushTab;
